/** Where pointers are over a PDF (PdfPointers.tsx): on its pages, in its points. */

import type { PointerSpace } from "../../office/sync/pointers.ts";

/**
 * The space of the PDF drawn in `scroller` at `zoom`: a point is on a page (its number), at an offset from its top
 * left corner in the PDF's points (whatever anyone's zoom); pages not drawn yet still have their size and place.
 */
export function pdfPointerSpace(scroller: () => HTMLElement | null, zoom: () => number): PointerSpace {
  const pages = () => [...(scroller()?.querySelectorAll<HTMLElement>("[data-page]") ?? [])];
  return {
    at(_x, y) {
      // The page under it, or the nearest down the column (in the gap between two, either).
      let best: { n: number; d: number } | null = null;
      for (const p of pages()) {
        const r = p.getBoundingClientRect();
        const d = y < r.top ? r.top - y : y > r.bottom ? y - r.bottom : 0;
        if (best === null || d < best.d) {
          best = { n: Number(p.dataset["page"]), d };
        }
      }
      return best ? { p: best.n } : null;
    },
    origin(anchor) {
      const n = (anchor as { p?: unknown } | null)?.p;
      const page = typeof n === "number" ? scroller()?.querySelector<HTMLElement>(`[data-page="${n}"]`) : null;
      const r = page?.getBoundingClientRect();
      return r ? { x: r.left, y: r.top, scale: zoom() } : null;
    },
    clip: () => scroller()?.getBoundingClientRect() ?? null,
    reveal(x, y) {
      const el = scroller();
      const r = el?.getBoundingClientRect();
      if (el && r) {
        el.scrollBy({ left: x - (r.left + r.right) / 2, top: y - (r.top + r.bottom) / 2, behavior: "smooth" });
      }
    },
  };
}
